import { Navigate, useSearchParams } from 'react-router-dom';
import useSWRImmutable from 'swr/immutable';
import { FCC } from '~/utils/types';
import { Card, Layout } from '../../shared';
import { SupportFooter } from '../../shared/support-footer/SupportFooter';
import { useBasket } from '../campaign/useBasket';
import { completeVerification } from '../campaign/verification/vipps/api';

export const CustomerVerificationCallback = () => {
  const [searchParams] = useSearchParams();
  const code = searchParams.get('code');
  const postVerificationRedirect = useBasket((s) => s.postVerificationRedirect);

  if (!postVerificationRedirect) {
    throw new Error('postVerificationRedirect is not set');
  }

  return <CustomerVerificationCallbackHandler code={code} postVerificationRedirect={postVerificationRedirect} />;
};

const CustomerVerificationCallbackHandler: FCC<{ code: string | null; postVerificationRedirect: string }> = ({
  code,
  postVerificationRedirect,
}) => {
  const selectedProvider = useBasket((s) => s.selectedVerificationProvider);
  const setVerificationResponse = useBasket((s) => s.setVerificationResponse);
  const { data, error } = useGetVerifiedUserInfo(code, selectedProvider, true);

  // Error happened or user cancelled the verification
  if (!code) {
    return <Navigate to={`${postVerificationRedirect}?restartRequired=true`} replace />;
  }

  // Error from our API, might be that user did not allow access to all fields we require
  if (error) {
    console.error('Could not get required data, please restart verification process', error);
    return <Navigate to={`${postVerificationRedirect}?restartRequired=true`} replace />;
  }

  if (data) {
    setVerificationResponse(data);
    return <Navigate to={postVerificationRedirect} replace />;
  }

  return <LoadingScreen />;
};

const LoadingScreen = () => (
  <Layout className="main-layout" focusOnRender wide={true}>
    <Card className="not-found">
      <h1>Venter på verifisering...</h1>
      <p className="top-text">
        Dette kan ta noen sekunder, vennligst ikke lukk denne siden. <br />
      </p>
      <SupportFooter displayHeading={false} />
    </Card>
  </Layout>
);

/**
 * @param useIsoBirthdate - set to `true` to use ISO date format for birthdate instead of default "ddMMyy"
 */
const useGetVerifiedUserInfo = (code: string | null, selectedProvider: string, useIsoBirthdate = false) => {
  return useSWRImmutable(code ? [code] : null, ([_code]) =>
    completeVerification(_code, selectedProvider, useIsoBirthdate),
  );
};
